import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  MagnifyingGlass, Plus, PencilSimple, Trash, Eye, FileText, UploadSimple,
  DownloadSimple, LockKey, Coins,
} from "@phosphor-icons/react";

const ROLE_BADGE = {
  admin: "bg-primary/10 text-primary border-primary/20",
  manager: "bg-warning/10 text-warning border-warning/20",
  supervisor: "bg-accent-foreground/10 text-accent-foreground border-border",
  employee: "bg-muted text-muted-foreground border-border",
};

const emptyForm = { email: "", name: "", role: "employee", employee_id: "", department: "", position: "", location: "", phone: "", join_date: "", employment_status: "active", resign_date: "", basic_salary: "", allowances: "", subsidiary: "", uniform_allowances: "", vl_credits: "", sl_credits: "", others_credits: "", loan_balance: "", password: "" };

export default function Directory() {
  const { isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detail, setDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showResigned, setShowResigned] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees", { params: { q: q || undefined, include_resigned: showResigned || undefined } });
      const lastName = (n) => {
        n = (n || "").trim();
        if (n.includes(",")) return n.split(",")[0].trim().toLowerCase();
        const parts = n.split(/\s+/);
        return (parts[parts.length - 1] || "").toLowerCase();
      };
      const sorted = [...data].sort((a, b) => {
        const ac = (a.employee_id || "").toUpperCase().includes("CEB") ? 1 : 0;
        const bc = (b.employee_id || "").toUpperCase().includes("CEB") ? 1 : 0;
        if (ac !== bc) return ac - bc;
        return lastName(a.name).localeCompare(lastName(b.name), undefined, { sensitivity: "base" });
      });
      setEmployees(sorted);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q, showResigned]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setFormOpen(true); };
  const openEdit = (emp) => {
    setEditing(emp);
    setForm({ ...emptyForm, ...emp, employment_status: emp.employment_status || "active", resign_date: emp.resign_date || "", basic_salary: emp.basic_salary ?? "", allowances: emp.allowances ?? "", subsidiary: emp.subsidiary ?? "", uniform_allowances: emp.uniform_allowances ?? "", vl_credits: emp.vl_credits ?? "", sl_credits: emp.sl_credits ?? "", others_credits: emp.others_credits ?? "", loan_balance: emp.loan_balance ?? "", password: "" });
    setFormOpen(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (form.employment_status === "resigned" && !form.resign_date) {
      toast.error("Please select the date of resignation.");
      return;
    }
    const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));
    const payload = { ...form, basic_salary: numOrNull(form.basic_salary), allowances: numOrNull(form.allowances), subsidiary: numOrNull(form.subsidiary), uniform_allowances: numOrNull(form.uniform_allowances), vl_credits: numOrNull(form.vl_credits), sl_credits: numOrNull(form.sl_credits), others_credits: numOrNull(form.others_credits), loan_balance: numOrNull(form.loan_balance) };
    try {
      if (editing) {
        const { password, email, ...rest } = payload;
        await api.put(`/employees/${editing.id}`, rest);
        if (password && password.trim()) {
          await api.put(`/employees/${editing.id}/password`, { password: password.trim() });
        }
        toast.success("Employee updated");
      } else {
        await api.post("/employees", payload);
        toast.success("Employee added");
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/employees/${deleteTarget.id}`);
      toast.success("Employee removed");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="directory-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">People</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Employee Directory</h1>
          <p className="text-muted-foreground mt-2">{employees.length} team member{employees.length !== 1 ? "s" : ""}</p>
        </div>
        {isAdmin && (
          <Button data-testid="add-employee-button" onClick={openCreate} className="rounded-lg gap-2 font-semibold">
            <Plus weight="bold" className="h-4 w-4" /> Add Employee
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="directory-search" placeholder="Search by name, email or ID..." className="pl-9"
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {isAdmin && (
          <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer select-none" data-testid="show-resigned-toggle-label">
            <Switch checked={showResigned} onCheckedChange={setShowResigned} data-testid="show-resigned-toggle" />
            Show resigned staff
          </label>
        )}
      </div>

      <Card className="border-border shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead>Employee</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : employees.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No employees found.</TableCell></TableRow>
              ) : employees.map((emp) => (
                <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`} className="animate-fade-up">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs shrink-0">
                        {emp.picture ? <img src={emp.picture} alt="" className="h-9 w-9 rounded-full object-cover" /> :
                          emp.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{emp.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{emp.email}</p>
                        {emp.employment_status === "resigned" && emp.resign_date && (
                          <p className="text-xs text-destructive truncate" data-testid={`resign-date-${emp.id}`}>Resigned {emp.resign_date}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{emp.employee_id || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{emp.department || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{emp.position || "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {emp.employment_status === "resigned" && (
                        <Badge variant="outline" className="font-medium bg-destructive/10 text-destructive border-destructive/20" data-testid={`resigned-badge-${emp.id}`}>Resigned</Badge>
                      )}
                      <Badge variant="outline" className={`capitalize font-medium ${ROLE_BADGE[emp.role]}`}>{emp.role}</Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`view-employee-${emp.id}`}
                        onClick={() => setDetail(emp)}><Eye className="h-4 w-4" /></Button>
                      {isAdmin && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-employee-${emp.id}`}
                            onClick={() => openEdit(emp)}><PencilSimple className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`delete-employee-${emp.id}`}
                            onClick={() => setDeleteTarget(emp)}><Trash className="h-4 w-4" /></Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Employee" : "Add Employee"}</DialogTitle>
            <DialogDescription>{editing ? "Update employee details." : "Create a new team member record."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-4 max-h-[60vh] overflow-y-auto pr-1" data-testid="employee-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label>Full name <span className="text-muted-foreground font-normal">(Surname, First name)</span></Label>
                <Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Dela Cruz, Juan" required />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Email</Label>
                <Input data-testid="form-email" type="email" value={form.email} disabled={!!editing}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label>Employee ID</Label>
                <Input data-testid="form-employee-id" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Employment status</Label>
                <Select value={form.employment_status} onValueChange={(v) => setForm({ ...form, employment_status: v, resign_date: v === "active" ? "" : form.resign_date })}>
                  <SelectTrigger data-testid="form-employment-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="resigned">Resigned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.employment_status === "resigned" && (
                <div className="space-y-1.5">
                  <Label>Date resigned <span className="text-destructive">*</span></Label>
                  <Input data-testid="form-resign-date" type="date" value={form.resign_date}
                    onChange={(e) => setForm({ ...form, resign_date: e.target.value })} required />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Department</Label>
                <Input data-testid="form-department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Position</Label>
                <Input data-testid="form-position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Location</Label>
                <Input data-testid="form-location" placeholder="e.g. Cebu, Manila" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input data-testid="form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Join date</Label>
                <Input data-testid="form-join-date" type="date" value={form.join_date} onChange={(e) => setForm({ ...form, join_date: e.target.value })} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="flex items-center gap-1.5"><LockKey className="h-3.5 w-3.5" /> Compensation Package (admin only)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Input data-testid="form-basic_salary" type="number" placeholder="Basic Salary" value={form.basic_salary} onChange={(e) => setForm({ ...form, basic_salary: e.target.value })} />
                    <p className="text-[11px] text-muted-foreground mt-1">Basic Salary</p>
                  </div>
                  <div>
                    <Input data-testid="form-allowances" type="number" placeholder="Allowances" value={form.allowances} onChange={(e) => setForm({ ...form, allowances: e.target.value })} />
                    <p className="text-[11px] text-muted-foreground mt-1">Allowances</p>
                  </div>
                  <div>
                    <Input data-testid="form-subsidiary" type="number" placeholder="Subsidiary" value={form.subsidiary} onChange={(e) => setForm({ ...form, subsidiary: e.target.value })} />
                    <p className="text-[11px] text-muted-foreground mt-1">Subsidiary</p>
                  </div>
                  <div>
                    <Input data-testid="form-uniform_allowances" type="number" placeholder="Uniform Allowances" value={form.uniform_allowances} onChange={(e) => setForm({ ...form, uniform_allowances: e.target.value })} />
                    <p className="text-[11px] text-muted-foreground mt-1">Uniform Allowances</p>
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><LockKey className="h-3.5 w-3.5" /> Leave credits remaining</Label>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Input data-testid="form-vl_credits" type="number" step="0.5" placeholder="VL" value={form.vl_credits} onChange={(e) => setForm({ ...form, vl_credits: e.target.value })} />
                    <p className="text-[11px] text-muted-foreground mt-1 text-center">VL</p>
                  </div>
                  <div>
                    <Input data-testid="form-sl_credits" type="number" step="0.5" placeholder="SL" value={form.sl_credits} onChange={(e) => setForm({ ...form, sl_credits: e.target.value })} />
                    <p className="text-[11px] text-muted-foreground mt-1 text-center">SL</p>
                  </div>
                  <div>
                    <Input data-testid="form-others_credits" type="number" step="0.5" placeholder="Others" value={form.others_credits} onChange={(e) => setForm({ ...form, others_credits: e.target.value })} />
                    <p className="text-[11px] text-muted-foreground mt-1 text-center">Others</p>
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><LockKey className="h-3.5 w-3.5" /> Loan balance</Label>
                <Input data-testid="form-loan_balance" type="number" step="0.01" value={form.loan_balance} onChange={(e) => setForm({ ...form, loan_balance: e.target.value })} />
              </div>
              {!editing && (
                <div className="space-y-1.5 col-span-2">
                  <Label>Temporary password (optional)</Label>
                  <Input data-testid="form-password" type="text" placeholder="Leave blank for Google-only login"
                    value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
              )}
              {editing && (
                <div className="space-y-1.5 col-span-2">
                  <Label>Reset password (optional)</Label>
                  <Input data-testid="form-reset-password" type="text" placeholder="Enter a new password to reset it"
                    value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-employee-button" className="font-semibold">{editing ? "Save changes" : "Add employee"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {detail && <EmployeeDetail employee={detail} isAdmin={isAdmin} onClose={() => setDetail(null)} />}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove employee?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <strong>{deleteTarget?.name}</strong> and their documents. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmployeeDetail({ employee, isAdmin, onClose }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadDocs = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get(`/employees/${employee.id}/documents`);
      setDocs(data);
    } catch (e) { /* ignore */ }
  }, [employee.id, isAdmin]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", "document");
    try {
      await api.post(`/employees/${employee.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      loadDocs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error("Failed to open document");
    }
  };

  const removeDoc = async (doc) => {
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Document removed");
      loadDocs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const row = (label, value) => (
    <div className="flex justify-between py-2.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right">{value || "—"}</span>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="employee-detail">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold">
              {employee.picture ? <img src={employee.picture} alt="" className="h-12 w-12 rounded-full object-cover" /> :
                employee.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
            </div>
            <div>
              <DialogTitle className="font-heading">{employee.name}</DialogTitle>
              <DialogDescription className="capitalize">{employee.position || employee.role}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto pr-1">
          <div className="mt-2">
            {row("Email", employee.email)}
            {row("Phone", employee.phone)}
            {row("Employee ID", employee.employee_id)}
            {row("Department", employee.department)}
            {row("Role", <span className="capitalize">{employee.role}</span>)}
            {row("Join date", employee.join_date)}
            {employee.employment_status === "resigned" && row("Date resigned", <span className="text-destructive font-semibold">{employee.resign_date || "—"}</span>)}
          </div>

          {isAdmin ? (
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <LockKey weight="duotone" className="h-4 w-4 text-primary" />
                <span className="uppercase text-xs tracking-[0.2em] font-medium text-primary">Confidential — Admin only</span>
              </div>
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4" data-testid="employee-compensation">
                <div className="flex items-center gap-1.5 mb-3">
                  <Coins className="h-4 w-4" />
                  <span className="text-sm font-medium">Compensation Package</span>
                </div>
                {(() => {
                  const items = [
                    ["Basic Salary", employee.basic_salary],
                    ["Allowances", employee.allowances],
                    ["Subsidiary", employee.subsidiary],
                    ["Uniform Allowances", employee.uniform_allowances],
                  ];
                  const total = items.reduce((s, [, v]) => s + (Number(v) || 0), 0);
                  const anySet = items.some(([, v]) => v != null && v !== "");
                  return (
                    <div className="space-y-2">
                      {items.map(([label, v]) => (
                        <div key={label} className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{label}</span>
                          <span className="font-medium" data-testid={`employee-${label.toLowerCase().replace(/ /g, "-")}`}>
                            {v != null && v !== "" ? `₱${Number(v).toLocaleString()}` : "—"}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-2 mt-1 border-t border-primary/20">
                        <span className="text-sm font-medium">Total Compensation</span>
                        <span className="text-lg font-heading font-bold" data-testid="employee-total-compensation">
                          {anySet ? `₱${total.toLocaleString()}` : "Not set"}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 mt-3" data-testid="employee-leave-credits">
                <p className="text-sm text-muted-foreground flex items-center gap-1.5 mb-2"><Coins className="h-4 w-4" /> Leave credits remaining</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><p className="text-lg font-heading font-bold text-primary" data-testid="vl-remaining">{employee.vl_credits ?? 0}</p><p className="text-[11px] text-muted-foreground">VL</p></div>
                  <div><p className="text-lg font-heading font-bold text-warning" data-testid="sl-remaining">{employee.sl_credits ?? 0}</p><p className="text-[11px] text-muted-foreground">SL</p></div>
                  <div><p className="text-lg font-heading font-bold" data-testid="others-remaining">{employee.others_credits ?? 0}</p><p className="text-[11px] text-muted-foreground">Others</p></div>
                </div>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">Documents</span>
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" onChange={handleUpload} data-testid="upload-document-input" disabled={uploading} />
                    <span className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline">
                      <UploadSimple className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload"}
                    </span>
                  </label>
                </div>
                <div className="space-y-2">
                  {docs.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-3 text-center border border-dashed border-border rounded-lg">
                      No documents yet.
                    </p>
                  ) : docs.map((doc) => (
                    <div key={doc.id} data-testid={`document-${doc.id}`}
                      className="flex items-center gap-3 p-2.5 rounded-lg border border-border">
                      <FileText weight="duotone" className="h-5 w-5 text-primary shrink-0" />
                      <span className="text-sm flex-1 truncate">{doc.original_filename}</span>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => download(doc)}
                        data-testid={`download-document-${doc.id}`}><DownloadSimple className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeDoc(doc)}
                        data-testid={`delete-document-${doc.id}`}><Trash className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-lg border border-dashed border-border bg-muted/40 p-4 flex items-center gap-3">
              <LockKey weight="duotone" className="h-5 w-5 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Compensation and documents are restricted to administrators.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
